import { useQuery } from '@tanstack/react-query';
import { materialsApi } from '@/features/catalog/components/admin/api';

/**
 * Internal code of the workshop's default material (PAL Melaminat EGGER, Alb
 * Clasic W960 ST7, 18mm). Used to pre-fill material selects on new item forms.
 * The actual DB id is looked up at runtime so the code works across
 * environments.
 *
 * The code comes from the materials Excel, so if that board is deleted or
 * re-imported under another code, every new form silently loses its default.
 */
export const DEFAULT_MATERIAL_INTERNAL_CODE = 'PAL-W960-ST7-18MM';

/**
 * Returns the DB id of the default material, or `''` while loading / if not
 * found. Safe to use as a form default — the autosuggest will resolve the
 * label from the id once it mounts.
 */
export function useDefaultMaterialId(): string {
  const query = useQuery({
    queryKey: ['catalog-materials', 'default', DEFAULT_MATERIAL_INTERNAL_CODE],
    queryFn: () =>
      materialsApi
        // Searched by the code itself: `search` also matches names and SKUs, and
        // several W960 boards (16mm, 18mm, offcuts) share both, so a broader term
        // would return whichever of them sorts first by name.
        .list({
          page: 1,
          pageSize: 10,
          active: true,
          includeDeleted: false,
          search: DEFAULT_MATERIAL_INTERNAL_CODE,
        })
        .then(
          (res) =>
            res.items.find((m) => m.internalCode === DEFAULT_MATERIAL_INTERNAL_CODE)?.id ?? null,
        ),
    staleTime: Infinity,
    meta: { silent: true },
  });
  return query.data ?? '';
}
